import React, { useState } from 'react';
import { Box, Checkbox, Grid, TextareaAutosize, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'store';
import ServiceRoleFormWithPartnerSearch from './ServiceRoleFormWithPartnerSearch';
import { setPartnerNotFound, setPartnerNotFoundComment } from 'reducers/partnerReducer';
import { getServiceRoleStepMetadata } from 'utils/json-form/formDataUtils';
import { MessageBox } from 'components/common/MessageBox';
import { ServiceRole } from 'pco-product-service-internal-consumer';

type ProductServiceRoleConfigurationProps = {
  setLocalServiceRole: React.Dispatch<React.SetStateAction<ServiceRole | null>>;
  setLocalPresentServiceRole: React.Dispatch<React.SetStateAction<ServiceRole | null>>;
  setServiceRoleCreated: React.Dispatch<React.SetStateAction<boolean>>;
  setRoleModified: React.Dispatch<React.SetStateAction<boolean>>;
};

export const ProductServiceRoleConfiguration = ({
  setLocalServiceRole,
  setLocalPresentServiceRole,
  setServiceRoleCreated,
  setRoleModified,
}: ProductServiceRoleConfigurationProps) => {
  const selectedServiceAgreement = useSelector((state: RootState) => state.serviceAgreements.selectedServiceAgreement);
  const [checked, setChecked] = useState<boolean>(false);
  const dispatch = useDispatch<AppDispatch>();
  const partnerNotFoundForServiceRole = useSelector((state: RootState) => state.partner.partnerNotFoundForServiceRole);

  const serviceRoleStepMetadata = selectedServiceAgreement
    ? getServiceRoleStepMetadata(selectedServiceAgreement)
    : undefined;

  const maxServiceRoleInstances = serviceRoleStepMetadata?.serviceRoleList?.[0]?.maximumInstances;

  return (
    <Box data-testid="product-service-role-configuration">
      {maxServiceRoleInstances && maxServiceRoleInstances > -1 && (
        <MessageBox
          message={`Maximum amount of related Service Role instances for this Service Agreement is limited to ${maxServiceRoleInstances}`}
          messageType={'info'}
          hasBorder={true}
        />
      )}

      <Box
        sx={{
          display: 'flex',
          justifyItems: 'center',
          flexDirection: 'column',
          mb: 1.5,
        }}
      >
        <Grid item xs={12}>
          <Typography sx={{ mt: 2 }}>
            Partner cannot be found{' '}
            <Checkbox
              sx={{}}
              checked={checked}
              onChange={() => {
                dispatch(setPartnerNotFound(!checked));
                if (!checked) {
                  setLocalServiceRole(null);
                  setLocalPresentServiceRole(null);
                  setServiceRoleCreated(false);
                }
                setChecked(!checked);
              }}
            />
          </Typography>
        </Grid>
        {partnerNotFoundForServiceRole && (
          <Grid item xs={12}>
            <Typography data-testid="product-service-role-configuration-comment">Comment</Typography>
            <TextareaAutosize
              data-testid="product-service-role-configuration-text-area"
              onChange={(e) => dispatch(setPartnerNotFoundComment(e.target.value))}
              minRows={4}
              style={{ width: '100%' }}
            />
          </Grid>
        )}
      </Box>

      {serviceRoleStepMetadata &&
        maxServiceRoleInstances === -1 &&
        selectedServiceAgreement?.serviceRoles?.map(
          (serviceRole) =>
            serviceRole.status !== 'WIDTHDRAWN' &&
            serviceRole.status !== 'DELETED' &&
            !partnerNotFoundForServiceRole && (
              <ServiceRoleFormWithPartnerSearch
                setRoleModified={setRoleModified}
                setLocalServiceRole={setLocalServiceRole}
                key={serviceRole.serviceRoleUid}
                isReadOnly={true}
                serviceRole={serviceRole}
              />
            ),
        )}

      {serviceRoleStepMetadata && maxServiceRoleInstances === -1 && !partnerNotFoundForServiceRole && (
        <ServiceRoleFormWithPartnerSearch
          setRoleModified={setRoleModified}
          setLocalServiceRole={setLocalServiceRole}
          isReadOnly={false}
        />
      )}

      {serviceRoleStepMetadata &&
        maxServiceRoleInstances &&
        maxServiceRoleInstances > 0 &&
        !partnerNotFoundForServiceRole && (
          <ServiceRoleFormWithPartnerSearch
            setRoleModified={setRoleModified}
            setServiceRoleCreated={setServiceRoleCreated}
            setLocalServiceRole={setLocalServiceRole}
            setLocalPresentServiceRole={setLocalPresentServiceRole}
            isReadOnly={false}
            serviceRole={selectedServiceAgreement?.serviceRoles ? selectedServiceAgreement?.serviceRoles[0] : undefined}
          />
        )}
    </Box>
  );
};
